import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Listing, PaymentPlanKind } from '@shared/api-types.js';
import { useCreateBooking, useQuote } from '../hooks/useBookings.js';
import { useMe } from '../hooks/useAuth.js';
import { useIdentityStatus } from '../hooks/useIdentity.js';
import { ApiError } from '../lib/api.js';
import { SpinnerButton } from './SpinnerButton.js';

interface Props {
  listing: Listing;
  isOwner: boolean;
  onEdit?: () => void;
  /** When rendered as a mobile bottom sheet, parent passes a close handler */
  onClose?: () => void;
}

function todayInputValue(daysAhead = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  d.setHours(10, 0, 0, 0);
  return d.toISOString().slice(0, 16);
}

const PLAN_LABEL: Record<PaymentPlanKind, string> = {
  full: 'Pay in full',
  half_now_half_later: 'Half now, half 7 days before pickup',
  custom: 'Pick your own installment dates',
  defer_until_pickup: '$0 up front — pay 7 days before pickup',
};

// Mirrors MIN_DAYS_FOR_HALF / MIN_DAYS_FOR_CUSTOM / MIN_DAYS_FOR_DEFER in
// src/server/booking/pricing.ts. If those constants change, update these too —
// they're for UX hints only; the server still enforces eligibility.
const PLAN_MIN_DAYS: Record<PaymentPlanKind, number> = {
  full: 0,
  half_now_half_later: 8,
  custom: 6,
  defer_until_pickup: 8,
};
const PLAN_INTEREST: Record<PaymentPlanKind, string> = {
  full: '0% — no interest',
  half_now_half_later: '2.99% on the deferred half',
  custom: '4.99% on the deferred balance',
  defer_until_pickup: '6.79% on the full amount',
};
const PLAN_DEFAULT_POLICY =
  'If a scheduled payment fails we email you and retry twice over the next few days. After three failures the booking is cancelled and the interest is retained.';
const ALL_PLANS: PaymentPlanKind[] = [
  'full',
  'half_now_half_later',
  'custom',
  'defer_until_pickup',
];

// Pricing.CUSTOM_PAID_BEFORE_DAYS — mirror for UX.
const CUSTOM_PAID_BEFORE_DAYS = 5;

function toDateInputValue(d: Date): string {
  const z = new Date(d);
  z.setHours(12, 0, 0, 0); // noon to dodge TZ rollover
  return z.toISOString().slice(0, 10);
}

export function BookingWidget({ listing, isOwner, onEdit, onClose }: Props) {
  const navigate = useNavigate();
  const me = useMe();
  const identity = useIdentityStatus();
  const create = useCreateBooking();

  const [startsAt, setStartsAt] = useState(() => todayInputValue(2));
  const [endsAt, setEndsAt] = useState(() => todayInputValue(3));
  const [plan, setPlan] = useState<PaymentPlanKind>('full');
  // Renter-chosen installment dates for PLAN_CUSTOM (ISO YYYY-MM-DD).
  const [customDates, setCustomDates] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | undefined>();

  // Cutoff: all custom installments must clear ≥5 days before pickup.
  const customCutoff = useMemo(() => {
    const s = new Date(startsAt);
    if (!Number.isFinite(s.getTime())) return null;
    const c = new Date(s);
    c.setDate(c.getDate() - CUSTOM_PAID_BEFORE_DAYS);
    return c;
  }, [startsAt]);

  const customDatesIso = useMemo(
    () =>
      customDates
        .filter(Boolean)
        .map((d) => {
          const x = new Date(`${d}T12:00:00`);
          return Number.isFinite(x.getTime()) ? x.toISOString() : null;
        })
        .filter((d): d is string => d !== null),
    [customDates],
  );

  const quoteInput = useMemo(() => {
    try {
      const s = new Date(startsAt);
      const e = new Date(endsAt);
      if (!Number.isFinite(s.getTime()) || !Number.isFinite(e.getTime())) return null;
      if (e <= s) return null;
      // Don't even ask the server for a quote if the custom plan has no valid dates yet —
      // it'll just 400. Default back to FULL pricing for the preview in that case.
      const planForQuote: PaymentPlanKind =
        plan === 'custom' && customDatesIso.length === 0 ? 'full' : plan;
      return {
        listingId: listing.id,
        startsAt: s.toISOString(),
        endsAt: e.toISOString(),
        paymentPlan: planForQuote,
        customInstallmentDates: planForQuote === 'custom' ? customDatesIso : undefined,
      };
    } catch {
      return null;
    }
  }, [startsAt, endsAt, plan, customDatesIso, listing.id]);

  const quoteQuery = useQuote(quoteInput);
  const quote = quoteQuery.data?.quote;

  // Block any pickup/return that overlaps the owner's published blackouts.
  // Server enforcement is TODO — for v1 we trust the UI to prevent submission.
  const overlapBlackout = useMemo(() => {
    if (!listing.unavailability?.length) return null;
    const s = new Date(startsAt);
    const e = new Date(endsAt);
    if (!Number.isFinite(s.getTime()) || !Number.isFinite(e.getTime())) return null;
    for (const r of listing.unavailability) {
      const rf = new Date(`${r.from}T00:00:00`);
      const rt = new Date(`${r.to}T23:59:59`);
      if (s <= rt && e >= rf) {
        return r;
      }
    }
    return null;
  }, [listing.unavailability, startsAt, endsAt]);

  if (isOwner) {
    return (
      <div className="card p-6 lg:sticky lg:top-6">
        <p className="text-navy-700 mb-4">This is your trailer. Renters see the booking form here.</p>
        <button className="btn-secondary w-full" onClick={onEdit}>
          Edit your listing
        </button>
      </div>
    );
  }

  async function handleBook() {
    setError(undefined);
    if (!quoteInput) {
      setError('Please pick valid pickup and return dates.');
      return;
    }
    if (!me.data?.user) {
      navigate(`/login?next=/listings/${listing.slug}`);
      return;
    }
    if (!identity.data?.verified) {
      navigate(`/account/verify?next=/listings/${listing.slug}`);
      return;
    }
    if (plan === 'custom' && customDatesIso.length === 0) {
      setError('Pick at least one installment date.');
      return;
    }
    try {
      const result = await create.mutateAsync({ ...quoteInput, message: message || undefined });
      if (result.requiresPayment) {
        navigate(`/checkout/${result.transaction.id}`);
      } else {
        navigate(`/transactions/${result.transaction.id}?just=requested`);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'identity_not_verified') {
        navigate(`/account/verify?next=/listings/${listing.slug}`);
        return;
      }
      setError(err instanceof ApiError ? err.code : 'Could not create booking. Try again.');
    }
  }

  return (
    <div className="card p-6 lg:sticky lg:top-6">
      <div className="flex items-baseline justify-between gap-2 mb-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-navy">
            ${(listing.dailyRateCents / 100).toFixed(0)}
          </span>
          <span className="text-navy-500">/ day</span>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden w-9 h-9 -mr-1 flex items-center justify-center rounded-full hover:bg-navy-50 text-navy-500"
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>

      <div className="space-y-3 mb-4">
        <Field label="Pickup">
          <input
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Return">
          <input
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            className="input"
          />
        </Field>
        <Field label="Payment plan" hint="Pay in full, or spread it out — buy-now-pay-later with interest, like a car-rental hold.">
          <div className="space-y-2">
            {ALL_PLANS.map((p) => {
              const eligible = !quote || quote.eligiblePlans.includes(p);
              const isSelected = plan === p;
              return (
                <label
                  key={p}
                  className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-orange bg-orange/5'
                      : eligible
                        ? 'border-navy-100 hover:border-navy-300 bg-white'
                        : 'border-navy-100 bg-navy-50/40 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <input
                    type="radio"
                    name="payment-plan"
                    className="mt-1 h-4 w-4 text-orange focus:ring-orange/30"
                    checked={isSelected}
                    disabled={!eligible}
                    onChange={() => eligible && setPlan(p)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-navy">{PLAN_LABEL[p]}</span>
                      <span className="text-[10px] uppercase tracking-wider text-navy-500">
                        {PLAN_INTEREST[p]}
                      </span>
                    </div>
                    {!eligible && PLAN_MIN_DAYS[p] > 0 && (
                      <p className="text-xs text-navy-500 mt-0.5">
                        Available when pickup is at least {PLAN_MIN_DAYS[p]} days away.
                      </p>
                    )}
                    {eligible &&
                      isSelected &&
                      quote &&
                      quote.paymentPlan === p &&
                      quote.installments.length > 1 && (
                        <p className="text-xs text-navy-600 mt-0.5">
                          {quote.installments.length} payments — first today, then{' '}
                          {quote.installments
                            .slice(1)
                            .map((inst) =>
                              new Date(inst.scheduledAt).toLocaleDateString('en-CA', {
                                month: 'short',
                                day: 'numeric',
                              }),
                            )
                            .join(', ')}
                          .
                        </p>
                      )}
                    {p !== 'full' && isSelected && (
                      <p className="text-[11px] text-navy-500 mt-1 leading-snug">
                        {PLAN_DEFAULT_POLICY}
                      </p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </Field>
        {plan === 'custom' && customCutoff && (
          <CustomInstallmentEditor
            dates={customDates}
            onChange={setCustomDates}
            cutoff={customCutoff}
          />
        )}
        {!listing.instantBookable && (
          <Field label="Message to owner (optional)">
            <textarea
              className="input min-h-[80px]"
              placeholder="What are you using the trailer for?"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={1000}
            />
          </Field>
        )}
      </div>

      {quote && (
        <div className="border-t border-navy-50 pt-4 mb-4 text-sm space-y-1.5">
          <Row
            label={`Rental (${quote.durationDays} day${quote.durationDays === 1 ? '' : 's'})`}
            value={money(quote.baseCents)}
          />
          {quote.insuranceCents > 0 && (
            <Row label="Protection plan" value={money(quote.insuranceCents)} />
          )}
          {/* Renter fee, interest, deposit follow below. */}
          <Row label="Service fee (15%)" value={money(quote.renterFeeCents)} />
          {/* Booking fee was a separate $10 line — merged into the protection
              plan. Keeping the field in the response for back-compat; we just
              don't render it. */}
          {quote.interestCents > 0 && (
            <Row
              label={`Payment plan interest (${
                quote.paymentPlan === 'custom'
                  ? '4.99%'
                  : quote.paymentPlan === 'defer_until_pickup'
                    ? '6.79%'
                    : '2.99%'
              })`}
              value={money(quote.interestCents)}
            />
          )}
          {quote.hstCents > 0 && (
            <Row label="HST (13%)" value={money(quote.hstCents)} />
          )}
          <Row label="Refundable deposit" value={money(quote.depositCents)} muted />
          <div className="border-t border-navy-50 pt-2 mt-2">
            <Row label="Total" value={money(quote.totalCents)} bold />
            {quote.paymentPlan !== 'full' && quote.installments.length > 0 && (
              <p className="text-xs text-navy-500 mt-1">
                Charged today: {money(quote.installments[0].amountCents)}.{' '}
                {quote.installments.length - 1} more installment
                {quote.installments.length - 1 === 1 ? '' : 's'} on file.
              </p>
            )}
          </div>
        </div>
      )}

      {quoteQuery.isError && (
        <p className="text-sm text-red-600 mb-3">Couldn't price that range — try different dates.</p>
      )}

      {overlapBlackout && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          The owner has blocked off {overlapBlackout.from} → {overlapBlackout.to}. Pick
          different dates to book.
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <SpinnerButton
        type="button"
        className="btn-primary w-full"
        loading={create.isPending}
        disabled={!quote || Boolean(overlapBlackout)}
        onClick={handleBook}
      >
        {create.isPending
          ? 'Creating booking…'
          : listing.instantBookable
            ? 'Book now'
            : 'Request to book'}
      </SpinnerButton>
      <p className="text-xs text-navy-400 text-center mt-3">
        You won't be charged until {listing.instantBookable ? 'after this step' : 'the owner accepts'}.
        $1,500 Trailer Guarantee + refundable deposit included.
      </p>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-navy-500 mb-1">
        {label}
      </span>
      {children}
      {hint && <span className="block text-xs text-navy-500 mt-1.5">{hint}</span>}
    </label>
  );
}

function Row({
  label,
  value,
  bold,
  muted,
}: {
  label: string;
  value: string;
  bold?: boolean;
  muted?: boolean;
}) {
  return (
    <div className="flex justify-between">
      <span className={muted ? 'text-navy-400' : 'text-navy-500'}>{label}</span>
      <span
        className={bold ? 'font-bold text-navy' : muted ? 'text-navy-500' : 'text-navy-700'}
      >
        {value}
      </span>
    </div>
  );
}

function money(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function CustomInstallmentEditor({
  dates,
  onChange,
  cutoff,
}: {
  dates: string[];
  onChange: (next: string[]) => void;
  cutoff: Date;
}) {
  const cutoffStr = toDateInputValue(cutoff);
  const tomorrowStr = toDateInputValue(new Date(Date.now() + 86_400_000));
  const cutoffPretty = cutoff.toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  function update(idx: number, value: string) {
    const next = [...dates];
    next[idx] = value;
    onChange(next);
  }
  function add() {
    onChange([...dates, '']);
  }
  function remove(idx: number) {
    onChange(dates.filter((_, i) => i !== idx));
  }

  return (
    <div className="rounded-lg border border-orange/30 bg-orange/5 p-3 space-y-2">
      <p className="text-xs text-navy-700 leading-snug">
        Pick when you want to settle up. The first installment is charged today; every
        date you add below must clear <strong>by {cutoffPretty}</strong> (5 days before
        pickup). You can space them however you like.
      </p>
      {dates.length === 0 && (
        <p className="text-xs text-navy-500 italic">
          No installments yet — add at least one to get a quote.
        </p>
      )}
      {dates.map((d, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <span className="text-xs text-navy-500 w-16 flex-shrink-0">
            Payment #{idx + 2}
          </span>
          <input
            type="date"
            className="input flex-1 py-1.5 text-sm"
            min={tomorrowStr}
            max={cutoffStr}
            value={d}
            onChange={(e) => update(idx, e.target.value)}
          />
          <button
            type="button"
            onClick={() => remove(idx)}
            className="text-xs text-red-600 hover:underline px-1"
            aria-label="Remove this installment"
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        disabled={dates.length >= 19}
        className="text-xs font-semibold text-orange hover:underline disabled:opacity-50"
      >
        + Add installment
      </button>
    </div>
  );
}
